import { useState, useEffect, useCallback } from 'react'
import { API_BASE_URL } from '../../../../config/api'

export default function useEventFormController({ navigate }) {
  const styles = {
    sectionWrapper: {
      background: 'white',
      borderRadius: '16px',
      padding: '10px 14px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e5e7eb',
      margin: '10px auto',
      width: '100%',
      maxWidth: '1080px', // match the (slightly smaller) outer shell width
      // Let inner card grow naturally; outer shell controls overall max-height
      overflowY: 'visible',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: '#111827',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
    },
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '8px',
    },
    formCol: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    label: {
      fontWeight: 600,
      fontSize: '13px',
      color: '#374151',
    },
    input: {
      padding: '8px 10px',
      borderRadius: '10px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#d1d5db',
      fontSize: '13px',
      outline: 'none',
      backgroundColor: '#fff',
      width: '100%',
    },
    select: {
      padding: '8px 10px',
      borderRadius: '10px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#d1d5db',
      fontSize: '13px',
      outline: 'none',
      backgroundColor: '#fff',
      width: '100%',
    },
    textarea: {
      padding: '8px 10px',
      borderRadius: '10px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#d1d5db',
      fontSize: '13px',
      outline: 'none',
      backgroundColor: '#fff',
      width: '100%',
      resize: 'vertical',
    },
    inputError: {
      borderColor: '#ef4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.15)',
    },
    errorMessage: {
      color: '#ef4444',
      fontSize: '12px',
      marginTop: '4px',
    },
    navigationButtons: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '20px',
    },
    backButton: {
      background: '#eef2ff',
      color: '#3730a3',
      padding: '10px 14px',
      borderRadius: '10px',
      border: '1px solid #c7d2fe',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 600,
    },
    nextButton: {
      background: '#2563eb',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '10px',
      border: 'none',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: 700,
      justifyContent: 'center',
    },
    nextButtonHover: {
      background: '#1d4ed8',
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 20px rgba(37, 99, 235, 0.25)',
    },
  }

  const getTodayDateString = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getCurrentTimeString = () => {
    const now = new Date()
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
  }

  const computeDuration = (startDate, endDate, startTime, endTime) => {
    try {
      const start = new Date(`${startDate}T${startTime}`)
      const end = new Date(`${endDate}T${endTime}`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ''
      const diffMs = end - start
      if (diffMs < 0) return ''
      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
      return `${hours}h ${minutes}m`
    } catch (error) {
      console.error('Error computing duration:', error)
      return ''
    }
  }

  const [formData, setFormData] = useState(() => {
    return {
      name: '',
      location: '',
      startdate: '',
      enddate: '',
      start_time: '',
      end_time: '',
      deadlinedate: '',
      deadlinetime: '',
      type: '',
      capacity: 1,
      description: '',
      terms_and_conditions: '',
      duration: '',
    }
  })

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [isFormValid, setIsFormValid] = useState(true)
  const [selectedClientTypes, setSelectedClientTypes] = useState([])
  const [clientTypeSelection, setClientTypeSelection] = useState('')
  const [customClientType, setCustomClientType] = useState('')
  const [customEventType, setCustomEventType] = useState('')
  const [packages, setPackages] = useState([
    {
      selectType: 'Full Package',
      packageType: '',
      location: '',
      duration: '',
      startDate: '',
      endDate: '',
      pricing: '',
      details: '',
      typeOptions: ['Full Package', 'Day'],
    },
  ])
  const [tabs, setTabs] = useState([])
  const [coverImageFile, setCoverImageFile] = useState(null)
  const [bannerImageFile, setBannerImageFile] = useState(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentSection, setCurrentSection] = useState('eventInfo')
  const [isNextButtonHovered, setIsNextButtonHovered] = useState(false)
  // Background configuration for the editable cover image area
  const [backgroundMode, setBackgroundMode] = useState('solid') // 'solid' | 'gradient' | 'image'
  const [imageBgColor, setImageBgColor] = useState('#f0f0f0')
  const [imageBgColor2, setImageBgColor2] = useState('#000000')
  const [bgImageUrl, setBgImageUrl] = useState('')

  const debounce = (func, wait) => {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(null, args), wait)
    }
  }

  const createDate = (dateStr, timeStr = '') => {
    if (!dateStr) return null
    try {
      if (dateStr.includes('T')) return new Date(dateStr)
      const [year, month, day] = dateStr.split('-').map(Number)
      if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null
      if (!timeStr) return new Date(year, month - 1, day)
      const [hours, minutes] = timeStr.split(':').map(Number)
      return new Date(year, month - 1, day, hours || 0, minutes || 0)
    } catch (error) {
      console.error('Error creating date:', error)
      return null
    }
  }

  const validateEventInfoTab = () => {
    const newErrors = {}

    if (!formData.name?.trim()) newErrors.name = 'Event name is required'
    if (!formData.location?.formatted_address && !formData.location?.street_address) newErrors.location = 'Location is required'
    if (!formData.startdate) newErrors.startdate = 'Start date is required'
    if (!formData.enddate) newErrors.enddate = 'End date is required'
    if (!formData.start_time) newErrors.start_time = 'Start time is required'
    if (!formData.end_time) newErrors.end_time = 'End time is required'
    if (!formData.type?.trim()) newErrors.type = 'Event type is required'
    if (formData.type === 'Other' && !customEventType?.trim()) newErrors.type = 'Custom event type is required'
    if (!formData.capacity || formData.capacity < 1) newErrors.capacity = 'Capacity must be at least 1'
    if (!formData.description?.trim()) newErrors.description = 'Description is required'

    if (formData.startdate && formData.enddate && formData.startdate > formData.enddate) {
      newErrors.enddate = 'End date cannot be before start date'
    }

    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors }
  }

  const validateClientTypesTab = () => {
    const newErrors = {}

    if (!selectedClientTypes || selectedClientTypes.length === 0) {
      newErrors.attendees = 'At least one client type must be selected'
    }
    if (!coverImageFile) newErrors.cover_image = 'Cover image is required'
    if (!bannerImageFile) newErrors.banner_image = 'Banner image is required'

    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors }
  }

  // Step-level validation for the Packages & Tabs step.
  // Triggered when the user presses Next on this section. It validates
  // Tabs (name + content) and key Package fields, but only by setting
  // per-field error keys (no top-level summary errors).
  const validatePackagesTab = () => {
    const newErrors = {}

    // Tabs: ensure each tab has name + content
    tabs.forEach((tab, idx) => {
      if (!tab.name?.trim()) newErrors[`tab_name_${idx}`] = 'Please enter a tab name'
      if (!tab.content?.trim()) newErrors[`tab_content_${idx}`] = 'Please enter tab content'
    })

    // Packages: ensure each package has key fields filled and valid.
    packages.forEach((pkg, idx) => {
      if (!pkg.packageType?.trim()) {
        newErrors[`package_type_${idx}`] = 'Please enter a package name/type'
      }

      if (!pkg.selectType?.trim()) {
        newErrors[`package_selectType_${idx}`] = 'Please select a package type'
      }

      if (!pkg.startDate) {
        newErrors[`package_startDate_${idx}`] = 'Please select a start date'
      }

      if (!pkg.endDate) {
        newErrors[`package_endDate_${idx}`] = 'Please select an end date'
      }

      if (pkg.startDate && pkg.endDate) {
        const start = new Date(pkg.startDate)
        const end = new Date(pkg.endDate)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          newErrors[`package_startDate_${idx}`] = 'Invalid date format'
        } else if (end < start) {
          newErrors[`package_endDate_${idx}`] = 'End date cannot be before start date'
        }
      }

      if (!pkg.pricing || !/^\d*\.?\d{0,2}$/.test(pkg.pricing) || Number.parseFloat(pkg.pricing || '0') <= 0) {
        newErrors[`package_pricing_${idx}`] = 'Please enter a valid price greater than 0 (e.g. 123.45)'
      }
    })

    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors }
  }

  const validateForm = useCallback(() => {
    const newErrors = {}
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (touched.name && !formData.name?.trim()) newErrors.name = 'Event name is required'
    if (touched.location && !formData.location?.formatted_address && !formData.location?.street_address) newErrors.location = 'Location is required'
    if (touched.startdate) {
      if (!formData.startdate) {
        newErrors.startdate = 'Start date is required'
      } else {
        const startDate = new Date(formData.startdate)
        if (Number.isNaN(startDate.getTime())) {
          newErrors.startdate = 'Invalid date format'
        } else if (startDate < today) {
          newErrors.startdate = 'Start date cannot be before today'
        }
      }
    }
    if (touched.enddate) {
      if (!formData.enddate) {
        newErrors.enddate = 'End date is required'
      } else {
        const endDate = new Date(formData.enddate)
        if (Number.isNaN(endDate.getTime())) {
          newErrors.enddate = 'Invalid date format'
        }
      }
    }
    if (touched.start_time && !formData.start_time) newErrors.start_time = 'Start time is required'
    if (touched.end_time && !formData.end_time) newErrors.end_time = 'End time is required'
    if (touched.deadlinedate && !formData.deadlinedate) newErrors.deadlinedate = 'Registration deadline date is required'
    if (touched.deadlinetime && !formData.deadlinetime) newErrors.deadlinetime = 'Registration deadline time is required'
    if (touched.type && !formData.type?.trim()) newErrors.type = 'Event type is required'
    if (touched.type && formData.type === 'Other' && !customEventType?.trim())
      newErrors.type = "Custom event type is required when 'Other' is selected"
    if (touched.capacity && (!formData.capacity || formData.capacity <= 0)) newErrors.capacity = 'Capacity must be greater than 0'
    if (touched.description && !formData.description?.trim()) newErrors.description = 'Event details are required'
    if (touched.terms_and_conditions && !formData.terms_and_conditions?.trim())
      newErrors.terms_and_conditions = 'Terms and conditions are required'
    if (touched.duration && !formData.duration) newErrors.duration = 'Duration cannot be computed (check dates/times)'
    if (touched.attendees && selectedClientTypes.length === 0) newErrors.attendees = 'At least one client type is required'

    if (touched.startdate && touched.enddate && formData.startdate && formData.enddate) {
      const startDate = new Date(formData.startdate)
      const endDate = new Date(formData.enddate)
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        newErrors.startdate = 'Invalid date format'
      } else if (endDate < startDate) {
        newErrors.enddate = 'End date cannot be before start date'
      }
    }

    if (
      touched.startdate &&
      touched.enddate &&
      touched.start_time &&
      touched.end_time &&
      formData.startdate &&
      formData.enddate &&
      formData.start_time &&
      formData.end_time
    ) {
      const startTime = createDate(formData.startdate, formData.start_time)
      const endTime = createDate(formData.enddate, formData.end_time)
      if (endTime <= startTime) {
        newErrors.end_time = 'End time must be after start time'
      }
    }

    if (touched.deadlinedate && formData.deadlinedate) {
      const deadlineDate = new Date(formData.deadlinedate)
      if (Number.isNaN(deadlineDate.getTime())) {
        newErrors.deadlinedate = 'Invalid deadline date format'
      }
    }

    // Cover / banner image validation
    if (
      touched.cover_image &&
      (!coverImageFile || !(coverImageFile instanceof File) || !['image/png', 'image/jpeg'].includes(coverImageFile.type))
    ) {
      newErrors.cover_image = 'A valid PNG or JPEG cover image is required'
    }

    if (
      touched.banner_image &&
      (!bannerImageFile || !(bannerImageFile instanceof File) || !['image/png', 'image/jpeg'].includes(bannerImageFile.type))
    ) {
      newErrors.banner_image = 'A valid PNG or JPEG banner image is required'
    }

    // Tabs: inline per-field errors under specific fields
    tabs.forEach((tab, idx) => {
      if (touched[`tab_name_${idx}`] && !tab.name?.trim()) newErrors[`tab_name_${idx}`] = 'Please enter a tab name'
      if (touched[`tab_content_${idx}`] && !tab.content?.trim())
        newErrors[`tab_content_${idx}`] = 'Please enter tab content'
    })

    // Packages: inline per-field validation only (no top-level summaries).
    // This drives small inline messages / error styles directly under each
    // package input in the Packages card.
    packages.forEach((pkg, idx) => {
      if (touched[`package_type_${idx}`] === 'user_interaction' && !pkg.packageType?.trim()) {
        newErrors[`package_type_${idx}`] = 'Please enter a package name/type'
      }

      if (touched[`package_selectType_${idx}`] === 'user_interaction' && !pkg.selectType?.trim()) {
        newErrors[`package_selectType_${idx}`] = 'Please select a package type'
      }

      if (touched[`package_startDate_${idx}`] === 'user_interaction' && !pkg.startDate) {
        newErrors[`package_startDate_${idx}`] = 'Please select a start date'
      }

      if (touched[`package_endDate_${idx}`] === 'user_interaction' && !pkg.endDate) {
        newErrors[`package_endDate_${idx}`] = 'Please select an end date'
      }

      if (
        touched[`package_startDate_${idx}`] === 'user_interaction' &&
        touched[`package_endDate_${idx}`] === 'user_interaction' &&
        pkg.startDate &&
        pkg.endDate
      ) {
        const start = new Date(pkg.startDate)
        const end = new Date(pkg.endDate)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          newErrors[`package_startDate_${idx}`] = 'Invalid date format'
        } else if (end < start) {
          newErrors[`package_endDate_${idx}`] = 'End date cannot be before start date'
        }
      }

      if (
        touched[`package_pricing_${idx}`] === 'user_interaction' &&
        (!pkg.pricing || !/^\d*\.?\d{0,2}$/.test(pkg.pricing) || Number.parseFloat(pkg.pricing) <= 0)
      ) {
        newErrors[`package_pricing_${idx}`] = 'Please enter a valid price greater than 0 (e.g. 123.45)'
      }
    })

    setErrors(newErrors)
    setIsFormValid(Object.keys(newErrors).length === 0)
  }, [formData, customEventType, selectedClientTypes, tabs, packages, coverImageFile, bannerImageFile, touched])

  // Collect high-level validation messages for use in a popup/modal
  const collectAllValidationMessages = () => {
    const info = validateEventInfoTab()
    const packagesValidation = validatePackagesTab()

    return {
      information: Object.values(info.errors || {}),
      // For the simple EventForm page, we do not surface client type / banner
      // image errors in the popup, because that flow does not expose those
      // fields. Keep this empty so the modal shows "All good" for that
      // section even though the underlying wizard can still validate them
      // when used elsewhere.
      clientTypes: [],
      packages: Object.values(packagesValidation.errors || {}),
      terms: !formData.terms_and_conditions?.trim() ? ['Terms and conditions are required'] : [],
    }
  }

  const debouncedValidateForm = useCallback(debounce(validateForm, 300), [validateForm])

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      duration: computeDuration(prev.startdate, prev.enddate, prev.start_time, prev.end_time),
    }))
  }, [formData.startdate, formData.enddate, formData.start_time, formData.end_time])

  useEffect(() => {
    debouncedValidateForm()
  }, [formData, customEventType, selectedClientTypes, tabs, packages, coverImageFile, bannerImageFile, touched])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setTouched({ ...touched, [name]: true })
    debouncedValidateForm()
  }

  const handleBlur = (e) => {
    const { name } = e.target
    // For package fields we mark touched with a special flag so that
    // validateForm can attach inline errors only after user interaction.
    if (name && name.startsWith('package_')) {
      setTouched({ ...touched, [name]: 'user_interaction' })
    } else {
      setTouched({ ...touched, [name]: true })
    }
    debouncedValidateForm()
  }

  const handleEventTypeChange = (e) => {
    const value = e.target.value
    setFormData({ ...formData, type: value })
    setTouched({ ...touched, type: true })
    if (value !== 'Other') {
      setCustomEventType('')
    }
    debouncedValidateForm()
  }

  const handleCustomEventTypeChange = (e) => {
    setCustomEventType(e.target.value)
    setTouched({ ...touched, type: true })
    debouncedValidateForm()
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => {
      const updated = { ...prev, [name]: value }
      if (name === 'startdate' && prev.enddate < value) {
        updated.enddate = value
      }
      if (name === 'start_time' && prev.deadlinetime) {
        updated.deadlinetime = value
      }
      return updated
    })
    setTouched({ ...touched, [name]: true })
    debouncedValidateForm()
  }

  const handleNumberInput = (e) => {
    const { name, value } = e.target
    if (value === '' || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value === '' ? 1 : Number.parseInt(value, 10),
      }))
      setTouched({ ...touched, [name]: true })
      debouncedValidateForm()
    }
  }

  const removeCoverImage = () => {
    setCoverImageFile(null)
    setTouched({ ...touched, cover_image: true })
    debouncedValidateForm()
  }

  const removeBannerImage = () => {
    setBannerImageFile(null)
    setTouched({ ...touched, banner_image: true })
    debouncedValidateForm()
  }

  const handleClientTypeChange = (e) => {
    const value = e.target.value
    setClientTypeSelection(value)
    setTouched({ ...touched, attendees: true })
    if (value && value !== 'Other' && !selectedClientTypes.includes(value)) {
      setSelectedClientTypes((prev) => [...prev, value])
      setTimeout(() => setClientTypeSelection(''), 100)
      debouncedValidateForm()
    }
  }

  const handleAddCustomClientType = () => {
    const trimmedType = customClientType.trim()
    if (trimmedType && !selectedClientTypes.includes(trimmedType)) {
      setSelectedClientTypes([...selectedClientTypes, trimmedType])
      setCustomClientType('')
      setClientTypeSelection('')
      setTouched({ ...touched, attendees: true })
      debouncedValidateForm()
    } else if (selectedClientTypes.includes(trimmedType)) {
      alert('This client type already exists.')
    }
  }

  const handleRemoveClientType = (typeToRemove) => {
    setSelectedClientTypes(selectedClientTypes.filter((type) => type !== typeToRemove))
    setTouched({ ...touched, attendees: true })
    debouncedValidateForm()
  }

  const clearClientTypes = () => {
    setSelectedClientTypes([])
  }

  const addPackage = () => {
    setPackages([
      ...packages,
      {
        selectType: 'Full Package',
        packageType: '',
        location: '',
        duration: '',
        startDate: '',
        endDate: '',
        pricing: '',
        details: '',
        typeOptions: ['Full Package', 'Day'],
      },
    ])
    setTouched({ ...touched, packages: true })
    debouncedValidateForm()
  }

  const removePackage = (indexToRemove) => {
    setPackages((prev) => prev.filter((_, index) => index !== indexToRemove))
    setTouched({ ...touched, packages: true })
    debouncedValidateForm()
  }

  const handlePackageChange = (index, field, value) => {
    const updated = [...packages]
    if (field === 'pricing') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        updated[index][field] = value
      }
    } else if (field === 'duration') {
      if (value === '' || /^\d+$/.test(value)) {
        updated[index][field] = value
      }
    } else {
      updated[index][field] = value
    }
    setPackages(updated)
    // Normalize touched key names so they match the error keys and input names
    // used in PackagesSection. For the Package Name field, the state field is
    // `packageType` but the error key / input name is `package_type_${index}`.
    const baseKey =
      field === 'packageType' ? `package_type_${index}` : `package_${field}_${index}`
    setTouched({ ...touched, [baseKey]: 'user_interaction' })
    debouncedValidateForm()
  }

  const addTab = () => {
    setTabs([...tabs, { name: '', content: '' }])
    setTouched({ ...touched, tabs: true })
    debouncedValidateForm()
  }

  const removeTab = (indexToRemove) => {
    setTabs((prev) => prev.filter((_, index) => index !== indexToRemove))
    setTouched({ ...touched, tabs: true })
    debouncedValidateForm()
  }

  const handleTabChange = (index, field, value) => {
    const updated = [...tabs]
    updated[index][field] = value
    setTabs(updated)
    setTouched({ ...touched, [`tab_${field}_${index}`]: true })
    debouncedValidateForm()
  }

  const addDropdownOption = (index) => {
    const newOption = prompt('Enter new option:')
    if (newOption) {
      const updated = [...packages]
      updated[index].typeOptions.push(newOption)
      setPackages(updated)
      setTouched({ ...touched, packages: true })
      debouncedValidateForm()
    }
  }

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0]
    if (file && file instanceof File && ['image/png', 'image/jpeg'].includes(file.type) && file.size > 0) {
      setCoverImageFile(file)
      setTouched({ ...touched, cover_image: true })
      debouncedValidateForm()
    } else {
      alert('Please select a valid PNG or JPEG file for cover image.')
      setCoverImageFile(null)
      setTouched({ ...touched, cover_image: true })
      debouncedValidateForm()
    }
  }

  const handleBannerImageChange = (e) => {
    const file = e.target.files[0]
    if (file && file instanceof File && ['image/png', 'image/jpeg'].includes(file.type) && file.size > 0) {
      setBannerImageFile(file)
      setTouched({ ...touched, banner_image: true })
      debouncedValidateForm()
    } else {
      alert('Please select a valid PNG or JPEG file for banner image.')
      setBannerImageFile(null)
      setTouched({ ...touched, banner_image: true })
      debouncedValidateForm()
    }
  }

  const handleSendRequest = async () => {
    setShowSuccess(false)
    setIsSubmitting(true)

    const submitForm = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          alert('Please log in to create an event. Redirecting to login page...')
          navigate('/')
          return
        }

        const formDataToSend = new FormData()
        formDataToSend.append('name', formData.name)
        
        // Handle structured location data
        if (formData.location && typeof formData.location === 'object') {
          console.log('Frontend sending structured location data:', formData.location);
          // Store only street address in Events.Location field
          const streetAddressOnly = formData.location.street_address || '';
          formDataToSend.append('location', streetAddressOnly);
          formDataToSend.append('street_address', formData.location.street_address || '');
          formDataToSend.append('city', formData.location.city || '');
          formDataToSend.append('province_state', formData.location.province_state || '');
          formDataToSend.append('country', formData.location.country || '');
          formDataToSend.append('latitude', formData.location.latitude?.toString() || '');
          formDataToSend.append('longitude', formData.location.longitude?.toString() || '');
          
          console.log('FormData location fields being sent:', {
            location: streetAddressOnly,
            street_address: formData.location.street_address,
            city: formData.location.city,
            province_state: formData.location.province_state,
            country: formData.location.country,
            latitude: formData.location.latitude,
            longitude: formData.location.longitude
          });
        } else {
          // Fallback for string location (backward compatibility)
          console.log('Frontend sending string location data:', formData.location);
          formDataToSend.append('location', formData.location || '')
        }
        
        formDataToSend.append('startdate', formData.startdate)
        formDataToSend.append('enddate', formData.enddate)
        formDataToSend.append('time', formData.start_time)
        formDataToSend.append('endtime', formData.end_time)
        formDataToSend.append('deadlinedate', formData.deadlinedate)
        formDataToSend.append('deadlinetime', formData.deadlinetime)
        formDataToSend.append('type', formData.type === 'Other' ? customEventType : formData.type)
        formDataToSend.append('capacity', formData.capacity.toString())
        formDataToSend.append('description', formData.description)
        formDataToSend.append('terms_and_conditions', formData.terms_and_conditions)
        formDataToSend.append('duration', formData.duration)
        const attendeesData = JSON.stringify(Array.isArray(selectedClientTypes) ? selectedClientTypes : [])
        const tabsData = JSON.stringify(Array.isArray(tabs) ? tabs : [])
        const packagesData = JSON.stringify(Array.isArray(packages) ? packages : [])

        formDataToSend.append('attendees', attendeesData)
        formDataToSend.append('tabs', tabsData)
        formDataToSend.append('packages', packagesData)
        // Background configuration for CoverImages.Background
        formDataToSend.append('backgroundMode', backgroundMode)
        formDataToSend.append('imageBgColor', imageBgColor)
        formDataToSend.append('imageBgColor2', imageBgColor2)
        formDataToSend.append('bgImageUrl', bgImageUrl)
        if (coverImageFile) {
          formDataToSend.append('cover_image', coverImageFile)
        }
        if (bannerImageFile) {
          formDataToSend.append('banner_image', bannerImageFile)
        }

        const response = await fetch(`${API_BASE_URL}/organiser/events`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataToSend,
        })

        const result = await response.json()

        if (!response.ok) {
          throw new Error(result.error || result.details || 'Failed to create event')
        }

        setShowSuccess(true)
        setTimeout(() => {
          setShowSuccess(false)
          navigate('/organiser-dash')
        }, 3000)
      } catch (error) {
        console.error('Submission error:', error.message)
        setIsSubmitting(false)
      }
    }

    setTimeout(submitForm, 0)
  }

  const showEventInfo = () => setCurrentSection('eventInfo')

  const showClientTypes = () => {
    const validation = validateEventInfoTab()
    if (!validation.isValid) {
      const eventInfoTouched = {
        name: true,
        location: true,
        startdate: true,
        enddate: true,
        start_time: true,
        end_time: true,
        deadlinedate: true,
        deadlinetime: true,
        type: true,
        capacity: true,
        description: true,
      }
      setTouched((prev) => ({ ...prev, ...eventInfoTouched }))
      setErrors(validation.errors)
      return
    }
    setCurrentSection('clientTypes')
  }

  const showPackagesTabs = () => {
    const validation = validateClientTypesTab()
    if (!validation.isValid) {
      const clientTypesTouched = {
        attendees: true,
        cover_image: true,
        banner_image: true,
      }
      setTouched((prev) => ({ ...prev, ...clientTypesTouched }))
      setErrors(validation.errors)
      return
    }
    setCurrentSection('packagesTabs')
  }

  const showTerms = () => {
    const validation = validatePackagesTab()
    if (!validation.isValid) {
      const packagesTouched = { packages: true, tabs: true }

      // Mark relevant package fields as touched so inline errors appear
      packages.forEach((_, idx) => {
        packagesTouched[`package_type_${idx}`] = 'user_interaction'
        packagesTouched[`package_location_${idx}`] = 'user_interaction'
        packagesTouched[`package_selectType_${idx}`] = 'user_interaction'
        packagesTouched[`package_duration_${idx}`] = 'user_interaction'
        packagesTouched[`package_startDate_${idx}`] = 'user_interaction'
        packagesTouched[`package_endDate_${idx}`] = 'user_interaction'
        packagesTouched[`package_pricing_${idx}`] = 'user_interaction'
      })

      // Mark tab fields as touched so inline errors appear
      tabs.forEach((_, idx) => {
        packagesTouched[`tab_name_${idx}`] = true
        packagesTouched[`tab_content_${idx}`] = true
      })

      setTouched((prev) => ({ ...prev, ...packagesTouched }))
      setErrors(validation.errors)
      return
    }
    setCurrentSection('terms')
  }

  const eventTypeOptions = [
    'Conference',
    'Workshop',
    'Seminar',
    'Exhibition',
    'Concert',
    'Festival',
    'Corporate Event',
    'Sports Event',
    'Other',
  ]

  return {
    styles,
    formData,
    setFormData,
    errors,
    setErrors,
    touched,
    setTouched,
    isFormValid,
    selectedClientTypes,
    clientTypeSelection,
    customClientType,
    customEventType,
    packages,
    tabs,
    coverImageFile,
    bannerImageFile,
    showSuccess,
    isSubmitting,
    currentSection,
    isNextButtonHovered,
    setIsNextButtonHovered,
    eventTypeOptions,
    setClientTypeSelection,
    setCustomClientType,
    setCustomEventType,
    setSelectedClientTypes,
    setPackages,
    setTabs,
    collectAllValidationMessages,
    setCoverImageFile,
    setBannerImageFile,
    setShowSuccess,
    setIsSubmitting,
    setCurrentSection,
    // Background configuration for cover image area
    backgroundMode,
    setBackgroundMode,
    imageBgColor,
    setImageBgColor,
    imageBgColor2,
    setImageBgColor2,
    bgImageUrl,
    setBgImageUrl,
    handleInputChange,
    handleBlur,
    handleEventTypeChange,
    handleCustomEventTypeChange,
    handleDateChange,
    handleNumberInput,
    handleClientTypeChange,
    handleAddCustomClientType,
    handleRemoveClientType,
    clearClientTypes,
    removeCoverImage,
    removeBannerImage,
    addPackage,
    removePackage,
    handlePackageChange,
    addTab,
    removeTab,
    handleTabChange,
    addDropdownOption,
    handleCoverImageChange,
    handleBannerImageChange,
    handleSendRequest,
    showEventInfo,
    showClientTypes,
    showPackagesTabs,
    showTerms,
  }
}
